import { useMemo } from 'react';
import { TFunction } from 'i18next';

import { loaders } from 'entities';
import { useRouteParams } from 'routes';

import { Column, Row, Tabs, Skeleton } from 'components/Blocks';
import { GoBackButton } from 'components/Buttons';
import { EntityStatusTag } from 'components/Entities';

import { NoPackage } from './NoPackage';
import { MainPackageInfo } from './Main';
import { PackageEvents } from './Events';
import { PackageFiles } from './Files';

import { usePageTitle, useUrlStateV2 } from 'hooks';
import { PackageContext } from './context';

import { listGet } from 'utils/array';

const packageComponents: Record<string, typeof MainPackageInfo> = {
  main: MainPackageInfo,

  logs: PackageEvents,

  files: PackageFiles,
};

const tabs = [
  { getLabel: (t: TFunction) => t('main'), value: 'main' },
  { getLabel: (t: TFunction) => t('files'), value: 'files' },
  { getLabel: (t: TFunction) => t('logs'), value: 'logs' },
];

function PackageProfile() {
  const { packageId } = useRouteParams('packageProfile');

  const [{ tab }, setQuery] = useUrlStateV2({
    tab: {
      defaultValue: 'main',
      validate: (v) => !!listGet(tabs, v, 'value'),
    },
  });

  const { data, loading, error, overwrite, retry } = loaders.invoice.findPackage.use({
    params: {
      id: packageId,
    },

    reloadOnChangeRef: packageId,
  });

  usePageTitle(data?.name);

  const context = useMemo(
    () => ({
      data: data!,
      loading,
      overwrite,
      reload: retry,
    }),
    [data, loading, overwrite, retry],
  );

  if (error) return <NoPackage />;

  const Component = packageComponents[tab];

  return (
    <PackageContext.Provider value={context}>
      <Column gap="2">
        {loading ? (
          <Skeleton height={36} width={180} />
        ) : (
          <Row>
            <GoBackButton to="packages" />

            <h5 className="text-3xl font-medium leading-none text-primary">{data?.name}</h5>

            <EntityStatusTag
              target="package"
              status={data!.status}
              round={false}
              size="medium"
              className="self-end"
            />
          </Row>
        )}

        {!loading && <Tabs value={tab} onTabChange={(tab) => setQuery({ tab })} tabs={tabs} />}

        <span className="spacer" />

        {loading ? <Skeleton quantity={5} height={55} /> : Component && <Component />}
      </Column>
    </PackageContext.Provider>
  );
}

export default PackageProfile;
